// Import Lucide icons
import { DollarSign, Zap, ShieldCheck, CreditCard, Gift, Infinity, Users } from "lucide-react";
import AuthenticationForm from "../components/AuthenticationForm";
import Title from "../components/Title";
import { RoutePages } from "../ultils";

// Header Component
function Header() {
  return (
    <header className="bg-white shadow border-b">
      <Title page={RoutePages.HOME} title="Home" />
      <div className="container mx-auto flex flex-col md:flex-row items-center justify-between py-4 px-4">
        <div className="flex items-center gap-3">
          <img
            src={process.env.REACT_APP_URL + "/favicon.png"}
            alt="AK API Logo - Unofficial Facebook Graph API"
            className="w-9 h-9"
            loading="lazy"
          />
          <span className="text-2xl font-bold text-blue-800 tracking-tight">AK API</span>
        </div>
        <nav className="flex gap-6 mt-2 md:mt-0 text-base font-medium">
          <a href="#features" className="text-gray-700 hover:text-blue-700 transition">Features</a>
          <a href="#pricing" className="text-gray-700 hover:text-blue-700 transition">Pricing</a>
          <a href="#cta" className="text-gray-700 hover:text-blue-700 transition">Signup</a>
        </nav>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="container mx-auto px-4 py-16 flex flex-col items-center text-center">
      <h1 className="text-3xl md:text-4xl font-extrabold mb-6 text-blue-900 leading-tight tracking-tight">
        The Only <span className="text-blue-700">Unofficial Facebook Graph API Service</span> On Internet
      </h1>
      <h2 className="text-xl md:text-2xl font-semibold text-green-700 mb-4 tracking-wide">
        Cheapest, Fastest, Stablest FB API Access – Just <span className="text-blue-700">$0.0006/call</span>
      </h2>
      <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto text-gray-700">
        <strong>Free trial forever without credit card.</strong> Unlimited <span className="font-bold text-green-700">FB API access</span>.<br />
        <span className="font-semibold text-blue-900">Exclusive API service</span> for developers needing <span className="font-bold">facebook graph api unlimited</span>.<br />
        All payment methods accepted. No hidden fees.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center mb-2">
        <a
          href="#cta"
          className="inline-block bg-blue-700 hover:bg-blue-800 text-white font-semibold px-8 py-3 rounded-lg shadow-md transition text-lg tracking-wide"
        >
          Try Free Now
        </a>
        <a
          href="#pricing"
          className="inline-block bg-white border border-green-600 text-green-700 hover:bg-green-50 font-semibold px-8 py-3 rounded-lg shadow-md transition text-lg tracking-wide"
        >
          See Pricing
        </a>
      </div>
      {/* SEO keywords for Google bot */}
      <div className="sr-only">
        the only unofficial facebook graph api service on internet, free trial forever without credit card, cheapest fastest stablest, fb access, fb api access, $0.0006/call, exclusive API service, unofficial facebook graph api, unofficial facebook api, unofficial graph api, facebook graph api unlimited, facebook graph api, all payment methods accepted
      </div>
    </section>
  );
}

// Features Section with Lucide icons
function Features() {
  return (
    <section id="features" className="container mx-auto px-4 py-16">
      <h2 className="text-3xl font-bold text-center mb-12 text-blue-900 tracking-tight">
        Why Choose Our <span className="text-blue-700">Unofficial Facebook Graph API?</span>
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        <FeatureCard
          icon={<DollarSign size={32} className="text-green-600" />}
          title="Ultra Affordable"
          desc={<><span className="font-bold text-green-700">$0.0006 per call</span>. Cheapest on the market.</>}
        />
        <FeatureCard
          icon={<Zap size={32} className="text-blue-700" />}
          title="Fastest & Stablest"
          desc="Blazing fast, ultra-stable API. 99.99% uptime, optimized for scale."
        />
        <FeatureCard
          icon={<ShieldCheck size={32} className="text-yellow-600" />}
          title="Exclusive & Unofficial"
          desc={<>The only <span className="font-bold">unofficial facebook graph api</span> service available.</>}
        />
        <FeatureCard
          icon={<CreditCard size={32} className="text-gray-700" />}
          title="All Payment Methods"
          desc="Accepts all payment methods: bank, crypto, and more."
        />
        <FeatureCard
          icon={<Gift size={32} className="text-green-700" />}
          title="Free Forever"
          desc="Free trial forever without credit card. No time limit."
        />
        <FeatureCard
          icon={<Infinity size={32} className="text-blue-900" />}
          title="Unlimited FB API Access"
          desc={<>Unlimited <span className="font-bold">facebook graph api</span> calls for all users.</>}
        />
        <FeatureCard
          icon={<Users size={32} className="text-blue-600" />}
          title="24/7 Community Support"
          desc="Large, active user community and instant help from our support team."
          className="col-span-1 sm:col-span-2 lg:col-span-3"
        />
      </div>
    </section>
  );
}

// FeatureCard component for professional look
function FeatureCard({ icon, title, desc, className = "" }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-xl shadow-sm p-8 flex flex-col items-center ${className}`}>
      <div className="mb-3">{icon}</div>
      <h3 className="text-lg font-semibold mb-2 text-gray-900">{title}</h3>
      <p className="text-gray-600 text-center text-base">{desc}</p>
    </div>
  );
}


function Pricing() {
  return (
    <section id="pricing" className="container mx-auto px-4 py-16">
      <h2 className="text-3xl font-bold text-center mb-8 text-blue-900 tracking-tight">
        Simple & Transparent Pricing – <span className="text-green-700">$0.0006/call</span>
      </h2>
      <p className="text-center text-xl text-gray-500 mb-4">
        <span role="img" aria-label="Vietnam flag" className="mr-1">🇻🇳</span>
        <span className="font-bold text-red-600">Ưu đãi cho người Việt Nam: chỉ từ 10đ/call</span>
      </p>
      <div className="flex flex-col md:flex-row justify-center items-center gap-8">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-10 flex flex-col items-center w-full max-w-sm">
          <span className="text-gray-700 text-lg">from just</span>
          <span className="text-5xl font-extrabold text-green-700 mb-2">$0.0006</span>
          <span className="text-gray-700 text-lg">per API call</span>
          <span role="img" aria-label="Vietnam flag" className="mr-1">🇻🇳</span>
          <span className="text-base font-bold text-red-600 mb-4">(Vietnamese: chỉ từ 10đ/call)</span> {/* Thêm dòng này */}
          <ul className="text-gray-600 text-left mb-6 space-y-2 text-base">
            <li>✔️ Free trial forever without credit card</li>
            <li>✔️ Accepts <span className="font-semibold">all payment methods</span></li>
            <li>✔️ No hidden fees</li>
            <li>✔️ Unlimited <span className="font-semibold">facebook graph api</span> access</li>
            <li>✔️ Cheapest, fastest, stablest unofficial facebook api</li>
          </ul>
          <a href="#cta" className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-3 rounded-lg shadow-md transition font-semibold text-lg tracking-wide">Get Started</a>
        </div>
      </div>
      {/* SEO keywords for Google bot */}
      <div className="sr-only">
        exclusive API service, unofficial facebook graph api, unofficial facebook api, unofficial graph api, facebook graph api unlimited, facebook graph api, all payment methods accepted
      </div>
    </section>
  );
}

// Call-to-Action Section
function CTA() {
  return (
    <section id="cta" className="container mx-auto px-4 py-12 flex flex-col items-center">
      <AuthenticationForm />
    </section>
  );
}

export default function LandingPage() {
  return (
    <div className="bg-gradient-to-br from-blue-50 to-green-50 text-gray-800 min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Hero />
        <Features />
        <Pricing />
        <CTA />
      </main>
    </div>
  );
}
